import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { FaChevronLeft } from 'react-icons/fa';


import { 
  ChevronRight, 
  ChevronDown, 
  ChevronUp, 
  ExternalLink, 
  Filter, 
  Search, 
  X,
  Calendar,
  MapPin,
  Layers,
  Award,
  Star,
  Heart
} from 'lucide-react';
import { projects } from '../data';

function Projects() {
  // Core state
  const [expandedClient, setExpandedClient] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProjects, setFilteredProjects] = useState(projects);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // Advanced features
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [favorites, setFavorites] = useState([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [sorting, setSorting] = useState('newest');
  const [isLoading, setIsLoading] = useState(false);
  const [visibleProjects, setVisibleProjects] = useState(6);
  const [activeCategory, setActiveCategory] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);
  
  // Refs for scroll animations
  const heroRef = useRef(null);
  const projectsRef = useRef(null);
  
  // Optimized data collections
  const clients = [...new Set(projects.map((project) => project.client))];
  const services = [...new Set(projects.map((project) => project.serviceId))];
  const projectCategories = [
    { id: 'all', name: 'All Projects' },
    ...clients.map(client => ({ id: client, name: client, type: 'client' })),
  
  ];

  // Parallax effect with framer-motion
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);

  // Filtering and sorting logic
  useEffect(() => {
    setIsLoading(true);
    
    let result = [...projects];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(project => 
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.client.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply client/service filter
    if (activeFilter !== 'all') {
      if (clients.includes(activeFilter)) {
        result = result.filter(project => project.client === activeFilter);
      } else {
        result = result.filter(project => project.serviceId === parseInt(activeFilter));
      }
    }
    
    // Apply sorting
    if (sorting === 'newest') {
      result.sort((a, b) => b.id - a.id);
    } else if (sorting === 'oldest') {
      result.sort((a, b) => a.id - b.id);
    } else if (sorting === 'alphabetical') {
      result.sort((a, b) => a.title.localeCompare(b.title));
    }
    
    // Delay to show loading state
    setTimeout(() => {
      setFilteredProjects(result);
      setIsLoading(false);
    }, 600);
  }, [searchTerm, activeFilter, sorting]);

  // Load more projects
  const loadMoreProjects = () => {
    setIsLoading(true);
    setTimeout(() => {
      setVisibleProjects(prev => Math.min(prev + 6, filteredProjects.length));
      setIsLoading(false);
    }, 500);
  };

  // Toggle favorites
  const toggleFavorite = (e, projectId) => {
    e.stopPropagation();
    setFavorites(prev => 
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  // Project modal handling
  const openProjectModal = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  // Image navigation in modal
  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === selectedProject.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? selectedProject.images.length - 1 : prevIndex - 1
      );
    }
  };

  // Toggle search panel
  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => document.getElementById('project-search')?.focus(), 100);
    } else {
      setSearchTerm('');
    }
  };
  const goToPreviousProject = () => {
    if (selectedProject) {
      const currentIndex = filteredProjects.findIndex((p) => p.id === selectedProject.id);
      if (currentIndex > 0) {
        setSelectedProject(filteredProjects[currentIndex - 1]);
        setCurrentImageIndex(0);
      }
    }
  };

  const goToNextProject = () => {
    if (selectedProject) {
      const currentIndex = filteredProjects.findIndex((p) => p.id === selectedProject.id);
      if (currentIndex < filteredProjects.length - 1) {
        setSelectedProject(filteredProjects[currentIndex + 1]);
        setCurrentImageIndex(0);
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.08
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const fadeInUpVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { duration: 0.7, ease: "easeOut" } 
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">
      {/* Hero Section with Advanced Parallax */}
      <section 
        ref={heroRef}
        className="relative overflow-hidden h-[85vh] flex items-center"
      >
        <motion.div 
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(/assets/images/ESW/esw-cambridgeside-1.jpg)', 
            filter: 'brightness(0.5)',
            y: heroY,
            scale: heroScale
          }}
        />
        
        <div className="absolute inset-0 " />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="container mx-auto px-6 md:px-8 relative z-10 mt-20"
        >
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
            className="max-w-3xl"
          >
          
            
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 text-white tracking-tight">
              Transforming <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-300">Spaces</span> Through Innovation
            </h1>
            
            <p className="text-xl md:text-2xl max-w-2xl mb-10 text-gray-200 font-light leading-relaxed">
              Discover our collection of cutting-edge façade engineering and glazing solutions that redefine architectural possibilities.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <motion.a
                href="/services"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center bg-gradient-to-r from-sky-500 to-sky-500 text-white px-8 py-4 rounded-xl hover:shadow-lg transition-all duration-300 text-lg font-semibold"
              >
                Explore Our Services <ChevronRight className="ml-2 h-5 w-5" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-xl hover:bg-white/20 transition-all duration-300 text-lg font-semibold"
              >
                Get a Quote
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
        
        {/* Scroll indicator with motion animation */}
        <motion.div 
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
     
        </motion.div>
        
        {/* Floating stats badges */}
        <div className="absolute bottom-24 right-12 max-w-xs hidden lg:block">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-teal-400/20 p-2 rounded-lg">
                  <Award className="h-5 w-5 text-sky-500" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm">Projects Completed</p>
                  <p className="text-white text-xl font-bold">{projects.length}+</p>
                </div>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="flex items-center space-x-3">
                <div className="bg-teal-400/20 p-2 rounded-lg ml-3">
                  <Calendar className="h-5 w-5 text-sky-500" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm">Years Experience</p>
                  <p className="text-white text-xl font-bold">12+</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advanced Filter Bar */}
 <section className="py-6 bg-white shadow-lg sticky top-0 z-30">
  <div className="container mx-auto px-4 md:px-8">
    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
      <div className="flex items-center">
        <button 
          onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
          className="flex items-center gap-2 text-sky-500 font-medium mr-4 lg:hidden"
        >
          <Filter className="h-5 w-5" /> 
          {isFilterMenuOpen ? 'Hide Filters' : 'Show Filters'}
          {isFilterMenuOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        
        <div className="hidden lg:flex items-center gap-2">
          <span className="flex items-center text-sky-500 font-semibold">
            <Filter className="h-5 w-5 mr-2" /> Categories:
          </span>
          
          <div className="flex flex-wrap gap-2 ml-2">
            {projectCategories.slice(0, 4).map(category => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeFilter === category.id 
                    ? 'bg-sky-500 text-white shadow-md' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
            
            {projectCategories.length > 4 && (
              <div className="relative">
                <button
                  onClick={() => setActiveCategory(activeCategory ? null : 'more')}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center"
                >
                  More <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeCategory ? 'rotate-180' : ''}`} />
                </button>
                
                {activeCategory === 'more' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl p-3 z-10 w-48"
                  >
                    {projectCategories.slice(4).map(category => (
                      <button
                        key={category.id}
                        onClick={() => {
                          setActiveFilter(category.id);
                          setActiveCategory(null);
                        }}
                        className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all mb-1 last:mb-0 ${
                          activeFilter === category.id 
                            ? 'bg-indigo-100 text-sky-500' 
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {category.name}
                      </button>
                    ))}
               
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isFilterMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="w-full lg:hidden overflow-hidden"
          >
            <div className="pt-2 pb-4 grid grid-cols-2 gap-2">
              {projectCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    activeFilter === category.id 
                      ? 'bg-sky-500 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
              {/* Add Berger Window and Doors for mobile view too */}
              <button
                onClick={() => setActiveFilter('berger-windows-doors')}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeFilter === 'berger-windows-doors' 
                    ? 'bg-sky-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Berger Window and Doors
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="flex items-center gap-4 w-full lg:w-auto justify-between">
        {/* View mode toggle */}
        <div className="hidden md:flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-2 rounded-lg text-sm flex items-center ${
              viewMode === 'grid' 
                ? 'bg-white shadow-sm text-sky-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
            </svg>
            Grid
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 rounded-lg text-sm flex items-center ${
              viewMode === 'list' 
                ? 'bg-white shadow-sm text-sky-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7"></path>
            </svg>
            List
          </button>
        </div>
        
        {/* Sort dropdown */}
        <div className="relative hidden md:block">
          <button
            onClick={() => setActiveCategory(activeCategory === 'sort' ? null : 'sort')}
            className="flex items-center gap-1 text-gray-700 hover:text-sky-500 px-3 py-2 rounded-lg bg-gray-100 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"></path>
            </svg>
            {sorting === 'newest' ? ' Most Recent ' : 
             sorting === 'oldest' ? 'Most Dated ' : 'Alphabetical (A–Z) '}
            <ChevronDown className={`ml-1 h-3 w-3 transition-transform ${activeCategory === 'sort' ? 'rotate-180' : ''}`} />
          </button>
          
          {activeCategory === 'sort' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl p-3 z-10 w-48"
            >
              <button
                onClick={() => {
                  setSorting('newest');
                  setActiveCategory(null);
                }}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all mb-1 ${
                  sorting === 'newest' 
                    ? 'bg-indigo-100 text-sky-500' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                  Most Recent 
              </button>
              <button
                onClick={() => {
                  setSorting('oldest');
                  setActiveCategory(null);
                }}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all mb-1 ${
                  sorting === 'oldest' 
                    ? 'bg-indigo-100 text-sky-500' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                Most Dated
              </button>
              <button
                onClick={() => {
                  setSorting('alphabetical');
                  setActiveCategory(null);
                }}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  sorting === 'alphabetical' 
                    ? 'bg-indigo-100 text-sky-500' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                Alphabetical (A–Z) 

              </button>
            </motion.div>
          )}
        </div>
        
        {/* Search component */}
        <div className="relative flex-grow lg:flex-grow-0">
          <AnimatePresence>
            {isSearchOpen && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '100%', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center bg-gray-100 rounded-full overflow-hidden pr-3"
              >
                <input
                  id="project-search"
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-grow bg-transparent border-none px-4 py-2 focus:outline-none text-gray-700"
                />
                <button onClick={toggleSearch} className="text-gray-500 hover:text-sky-500">
                  <X className="h-5 w-5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          
          {!isSearchOpen && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleSearch}
              className="flex items-center bg-sky-500 text-white px-4 py-2 rounded-full hover:bg-sky-500 transition-all"
            >
              <Search className="h-5 w-5 mr-2" />
              Search
            </motion.button>
          )}
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Projects Section */}
      <section ref={projectsRef} className="py-16">
        <div className="container mx-auto px-4 md:px-8">
          {/* Status bar */}
          <div className="flex flex-wrap justify-between items-center mb-10">
            <div className="text-gray-700 font-medium">
              Showing <span className="text-sky-500 font-bold">{Math.min(visibleProjects, filteredProjects.length)}</span> of <span className="text-sky-500 font-bold">{filteredProjects.length}</span> projects
              {activeFilter !== 'all' && (
                <span className="ml-2">
                  in <span className="text-sky-500 font-bold">{projectCategories.find(cat => cat.id === activeFilter)?.name}</span>
                  <button 
                    onClick={() => setActiveFilter('all')}
                    className="ml-2 text-black hover:text-sky-500 underline underline-offset-2 font-normal"
                  >
                    Clear filter
                  </button>
                </span>
              )}
            </div>
            
            {/* Mobile sort button */}
            <button 
              onClick={() => setActiveCategory(activeCategory === 'mobile-sort' ? null : 'mobile-sort')}
              className="md:hidden flex items-center text-gray-700 hover:text-sky-500"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"></path>
              </svg>
              Sort
              <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeCategory === 'mobile-sort' ? 'rotate-180' : ''}`} />
            </button>
            
            {activeCategory === 'mobile-sort' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="w-full mt-4 bg-white rounded-xl shadow-xl p-3 z-10 md:hidden"
              >
                <button
                  onClick={() => {
                    setSorting('newest');
                    setActiveCategory(null);
                  }}
                  className={`block w-full text-left px-3 py-3 rounded-lg text-sm font-medium transition-all mb-1 ${
                    sorting === 'newest' 
                      ? 'bg-indigo-100 text-sky-500' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  Most Recent 
                </button>
                <button
                  onClick={() => {
                    setSorting('oldest');
                    setActiveCategory(null);
                  }}
                  className={`block w-full text-left px-3 py-3 rounded-lg text-sm font-medium transition-all mb-1 ${
                    sorting === 'oldest' 
                      ? 'bg-indigo-100 text-sky-500' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  Most Dated 
                </button>
                <button
                  onClick={() => {
                    setSorting('alphabetical');
                    setActiveCategory(null);
                  }}
                  className={`block w-full text-left px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                    sorting === 'alphabetical' 
                      ? 'bg-indigo-100 text-sky-500' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  Alphabetical (A–Z) 
                </button>
              </motion.div>
            )}
          </div>
          
          {isLoading ? (
            // Skeleton loader
            <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-8`}>
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
                  <div className="h-64 bg-gray-200" />
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded mb-2 w-1/4" />
                    <div className="h-6 bg-gray-200 rounded mb-4 w-3/4" />
                    <div className="h-4 bg-gray-200 rounded mb-2 w-full" />
                    <div className="h-4 bg-gray-200 rounded mb-6 w-2/3" />
                    <div className="h-10 bg-gray-200 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProjects.length > 0 ? (
            <>
              {viewMode === 'grid' ? (
                // Grid view
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                  {filteredProjects.slice(0, visibleProjects).map((project) => (
                <motion.div
                key={project.id}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                onHoverStart={() => setHoveredProject(project.id)}
                onHoverEnd={() => setHoveredProject(null)}
                className="bg-white rounded-xl shadow-xl overflow-hidden group cursor-pointer relative"
                onClick={() => openProjectModal(project)}
              >
                {/* Favorite button */}
              
                
                <div className="relative h-64 overflow-hidden">
                <img 
  src={project.images[0].src} 
  alt={project.title} 
  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
/>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-sky-500">{project.client}</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-sky-500">
                      {project.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-indigo-700 transition-colors">{project.title}</h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-500 text-sm">
                      <MapPin className="h-4 w-4 mr-1" />
                      {project.location}
                    </div>
                    
                    <div className="flex items-center text-sky-500 font-medium">
                      View Details <ChevronRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </div>
                
                {/* Hover overlay */}
                <AnimatePresence>
                  {hoveredProject === project.id && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                     className="absolute inset-0 bg-gradient-to-r from-indigo-900/50 to-teal-600/50 flex items-center justify-center p-6"
                    >
                      <div className="text-center">
                        <h3 className="text-2xl font-bold mb-2 text-white">{project.title}</h3>
                        <p className="text-indigo-200 mb-6">{project.shortDescription}</p>
                        <span className="inline-flex items-center px-4 py-2 rounded-lg bg-white text-sky-500 font-semibold">
                          Explore Project <ChevronRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          // List view
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
          >
            {filteredProjects.slice(0, visibleProjects).map((project) => (
              <motion.div
                key={project.id}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => openProjectModal(project)}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-64 h-48 relative">
                  <img 
  src={project.images[0].src} 
  alt={project.title} 
  className="w-full h-full object-cover "
/>
                  
                  </div>
                  
                  <div className="p-6 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-sky-500">{project.client}</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-sky-500">
                        {project.category}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{project.title}</h3>
                    
                    <p className="text-gray-600 mb-4">{project.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-gray-500 text-sm">
                        <MapPin className="h-4 w-4 mr-1" />
                        {project.location}
                      </div>
                      
                      <div className="flex items-center text-sky-500 font-medium">
                        View Details <ChevronRight className="ml-1 h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      
        {/* Load more button */}
        {visibleProjects < filteredProjects.length && (
          <div className="flex justify-center mt-12">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={loadMoreProjects}
              className="bg-white border-2 border-sky-500 text-sky-500 px-8 py-3 rounded-xl font-medium hover:bg-indigo-50 transition-colors flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  Load More Projects <ChevronDown className="ml-2 h-5 w-5" />
                </>
              )}
            </motion.button>
          </div>
        )}
      </>
    ) : (
      // No results
      <motion.div
        variants={fadeInUpVariants}
        initial="hidden"
        animate="visible"
        className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-2xl mx-auto"
      >
        <div className="bg-indigo-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Search className="h-10 w-10 text-sky-500" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">No projects found</h3>
        <p className="text-gray-600 mb-6">
          We couldn't find any projects matching your search criteria. Try adjusting your filters or search terms.
        </p>
        <button
          onClick={() => {
            setSearchTerm('');
            setActiveFilter('all');
          }}
          className="bg-sky-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-sky-500 transition-colors inline-flex items-center"
        >
          Clear All Filters <X className="ml-2 h-5 w-5" />
        </button>
      </motion.div>
    )}
  </div>
</section>

{/* Project Detail Modal */}
<AnimatePresence>
  {selectedProject && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative"
      >
        {/* Close button */}
        <button
          onClick={closeProjectModal}
          className="absolute top-6 right-6 z-10 bg-white/80 p-2 rounded-full backdrop-blur-sm transition-all hover:bg-white"
        >
          <X className="h-6 w-6 text-gray-900" />
        </button>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Image gallery */}
          <div className="relative h-80 lg:h-full">
            {selectedProject.images && selectedProject.images.length > 0 && (
              <img
                src={selectedProject.images[currentImageIndex].src}
                alt={selectedProject.title}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
              />
            )}
            
            {selectedProject.images && selectedProject.images.length > 1 && (
              <div className="absolute inset-x-0 bottom-0 p-4 flex items-center justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="bg-white/80 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-900" />
                </button>
                
                <div className="bg-white/80 backdrop-blur-sm px-3 py-2 rounded-full text-sm font-medium flex items-center justify-center">
                  {currentImageIndex + 1} / {selectedProject.images.length}
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="bg-white/80 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                >
                  <ChevronRight className="h-5 w-5 text-gray-900" />
                </button>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="p-6 md:p-10">
            <div className="flex items-center mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-sky-500 mr-2">
                {selectedProject.category}
              </span>
              <span className="text-sm font-medium text-sky-500">{selectedProject.client}</span>
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{selectedProject.title}</h2>
            
            <div className="flex items-center space-x-4 text-gray-500 text-sm mb-6">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {selectedProject.year}
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                {selectedProject.location}
              </div>
            </div>
            
            <p className="text-gray-600 mb-8 leading-relaxed">{selectedProject.fullDescription || selectedProject.description}</p>
            
            {/* Project details */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Services</div>
                  <div className="font-medium">{selectedProject.services || selectedProject.category}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Client</div>
                  <div className="font-medium">{selectedProject.client || "Completed"}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Location</div>
                  <div className="font-medium">{selectedProject.location || "Full Project"}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Year</div>
                  <div className="font-medium">{selectedProject.year || "6 months"}</div>
                </div>
              </div>
            </div>
            
            {/* Call to action */}
            <div className="flex flex-wrap gap-4">
              <a
                href="/contact"
                className="bg-sky-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-sky-500 transition-colors flex items-center"
              >
                Start Similar Project <ChevronRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Project navigation */}
        <div className="flex items-center justify-between bg-gray-50 p-4 border-t border-gray-200">
          <button
            onClick={goToPreviousProject}
            disabled={selectedProject && filteredProjects.findIndex((p) => p.id === selectedProject.id) === 0}
            className={`flex items-center text-gray-700 hover:text-sky-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <ChevronLeft className="h-5 w-5 mr-1" /> Previous Project
          </button>
          <button
            onClick={goToNextProject}
            disabled={
              selectedProject &&
              filteredProjects.findIndex((p) => p.id === selectedProject.id) === filteredProjects.length - 1
            }
            className={`flex items-center text-gray-700 hover:text-sky-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Next Project <ChevronRight className="h-5 w-5 ml-1" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

{/* Bottom CTA Section */}
<section className="py-16 bg-gradient-to-br from-sky-500 to-indigo-700 relative overflow-hidden">
  <div className="absolute inset-0 bg-pattern opacity-10"></div>
  
  <div className="container mx-auto px-4 md:px-8 relative z-10">
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Start Your Next Project?</h2>
      <p className="text-indigo-200 text-lg mb-8">
        Our team of expert engineers and designers are ready to bring your vision to life with cutting-edge façade solutions.
      </p>
      <div className="flex flex-wrap justify-center gap-4">
        <a
          href="/contact"
          className="bg-white text-sky-500 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors"
        >
          Request a Quote
        </a>
        <a
          href="/services"
          className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white/10 transition-colors"
        >
          Explore Services
        </a>
      </div>
    </div>
  </div>
</section>
</div>
);
}

export default Projects;